import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CognitoGroup } from '@respaldos/shared';
import { COGNITO_GROUPS } from '@respaldos/shared';
import type { SessionUser } from '../lib/api';
import {
  GROUP_LABELS,
  inviteAdminUser,
  listAdminUsers,
  resendAdminInvite,
  setAdminUserEnabled,
  setAdminUserGroup,
  type AdminUser,
} from '../lib/adminApi';

const STATUS_LABEL: Record<AdminUser['status'], string> = {
  pending: 'Pendiente',
  active: 'Activo',
  disabled: 'Deshabilitado',
  unknown: 'Desconocido',
};

export function AdminUsersPage({ user }: { user: SessionUser }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [email, setEmail] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [group, setGroup] = useState<CognitoGroup>('Marketing');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(() => {
    return listAdminUsers(user)
      .then(setUsers)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [user]);

  useEffect(() => {
    if (!user.groups.includes('Admins')) return;
    void refresh();
  }, [user, refresh]);

  if (!user.groups.includes('Admins')) {
    return (
      <div className="error-banner">
        Solo administradores pueden gestionar usuarios.
      </div>
    );
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy('invite');
    try {
      const created = await inviteAdminUser(user, email.trim(), group, {
        given_name: givenName,
        family_name: familyName,
      });
      setEmail('');
      setGivenName('');
      setFamilyName('');
      setNotice(
        `Invitación enviada a ${created.email}. Recibirán una contraseña temporal por correo.`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function run(key: string, action: () => Promise<unknown>, ok: string) {
    setError(null);
    setNotice(null);
    setBusy(key);
    try {
      await action();
      setNotice(ok);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await refresh().catch(() => undefined);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="admin-users">
      <nav className="breadcrumb">
        <strong>Usuarios</strong>
        <span className="sep">·</span>
        <span>Invitar y administrar acceso Cognito</span>
      </nav>

      <p className="admin-lead">
        Invite por correo con nombre y apellido, y asigne un solo grupo
        (Marketing, BackupsBO/PE/PY o Admins). Cognito guarda el nombre y envía
        una contraseña temporal con la plantilla Respaldos.
      </p>

      {error && <div className="error-banner">{error}</div>}
      {notice && <div className="notice-banner">{notice}</div>}

      <form className="admin-invite" onSubmit={onInvite}>
        <h2>Invitar</h2>
        <div className="invite-row">
          <div className="grow">
            <label htmlFor="invite-email">Correo</label>
            <input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@grupogalero.com"
            />
          </div>
          <div className="name">
            <label htmlFor="invite-given">Nombre</label>
            <input
              id="invite-given"
              type="text"
              required
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
              placeholder="Ana"
              autoComplete="given-name"
              maxLength={80}
            />
          </div>
          <div className="name">
            <label htmlFor="invite-family">Apellido</label>
            <input
              id="invite-family"
              type="text"
              required
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="Rojas"
              autoComplete="family-name"
              maxLength={80}
            />
          </div>
          <div className="role">
            <label htmlFor="invite-group">Grupo</label>
            <select
              id="invite-group"
              value={group}
              onChange={(e) => setGroup(e.target.value as CognitoGroup)}
            >
              {COGNITO_GROUPS.map((g) => (
                <option key={g} value={g}>
                  {GROUP_LABELS[g]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy !== null}
          >
            Invitar
          </button>
        </div>
      </form>

      <div className="panel admin-table-wrap">
        <table className="file-table user-table">
          <thead>
            <tr>
              <th>Correo</th>
              <th>Nombre</th>
              <th>Grupo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Aún no hay usuarios.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const locked = busy !== null;
              return (
                <tr key={u.username}>
                  <td>{u.email}</td>
                  <td>{u.name || '—'}</td>
                  <td>
                    <select
                      aria-label={`Grupo de ${u.email}`}
                      value={u.group || 'Marketing'}
                      disabled={locked}
                      onChange={(e) => {
                        const next = e.target.value as CognitoGroup;
                        if (next === u.group) return;
                        void run(
                          u.username,
                          () => setAdminUserGroup(user, u.username, next),
                          `${u.email} ahora es ${GROUP_LABELS[next]}. Debe volver a iniciar sesión.`,
                        );
                      }}
                    >
                      {COGNITO_GROUPS.map((g) => (
                        <option key={g} value={g}>
                          {GROUP_LABELS[g]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span className={`user-badge ${u.status}`}>
                      {STATUS_LABEL[u.status]}
                    </span>
                  </td>
                  <td>
                    <div className="user-actions">
                      {u.status === 'pending' && (
                        <button
                          type="button"
                          className="btn"
                          disabled={locked}
                          onClick={() =>
                            void run(
                              u.username,
                              () => resendAdminInvite(user, u.username),
                              `Invitación reenviada a ${u.email}.`,
                            )
                          }
                        >
                          Reenviar invitación
                        </button>
                      )}
                      {u.enabled ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={locked}
                          onClick={() => {
                            if (
                              !window.confirm(
                                `¿Deshabilitar ${u.email}? No podrá iniciar sesión.`,
                              )
                            ) {
                              return;
                            }
                            void run(
                              u.username,
                              () => setAdminUserEnabled(user, u.username, false),
                              `${u.email} deshabilitado.`,
                            );
                          }}
                        >
                          Deshabilitar
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn"
                          disabled={locked}
                          onClick={() =>
                            void run(
                              u.username,
                              () => setAdminUserEnabled(user, u.username, true),
                              `${u.email} habilitado.`,
                            )
                          }
                        >
                          Habilitar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
